import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const POLICY_FIELDS = [
  ["block_downloads", "Block downloads"],
  ["block_history_clear", "Block clearing history"],
  ["block_incognito", "Block incognito / private"],
  ["block_screenshot", "Block screenshots"],
  ["block_print", "Block printing"],
  ["block_save", "Block saving pages"],
  ["block_devtools", "Block DevTools / inspect"],
  ["allow_right_click", "Allow right-click context menu (Inspect/DevTools always blocked)"],
  ["block_password_reveal", "Block 'show password' reveal on websites"],
  ["show_watermark", "Show watermark (employee name + date/time)"],
  ["email_tool_enabled", "Email Tool access (shared inbox)"],
  ["listing_mode_enabled", "AI Listing Mode (Amazon/Flipkart assistant)"],
];

export default function UserDetail() {
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [vault, setVault] = useState([]);
  const [history, setHistory] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [presets, setPresets] = useState([]);
  const [blacklistText, setBlacklistText] = useState("");
  const [savedMsg, setSavedMsg] = useState("");
  const [reveal, setReveal] = useState({});
  const [timeForm, setTimeForm] = useState({ enabled: true, start: "09:00", end: "19:00", tz: "Asia/Kolkata" });
  const [geoForm, setGeoForm] = useState({ enabled: false, lat: "", lng: "", radius: 100 });
  const [vaultForm, setVaultForm] = useState({ site: "", username: "", password: "", url: "", label: "" });
  const [blockForm, setBlockForm] = useState({
    label: "", url_pattern: "", match_type: "label", match_value: "", action: "both",
  });
  const todayISO = () => new Date().toISOString().slice(0, 10);
  const [activityDate, setActivityDate] = useState(todayISO());
  const [activity, setActivity] = useState(null);
  const [wmForm, setWmForm] = useState({ opacity: 0.15, size: 14 });

  const load = async () => {
    const [{ data: users }, { data: v }, { data: h }, { data: ov }, { data: eb }, { data: pp }] = await Promise.all([
      api.get("/admin/users"),
      api.get(`/admin/users/${id}/vault`),
      api.get(`/admin/users/${id}/history`),
      api.get(`/admin/users/${id}/geo-overrides`),
      api.get(`/admin/users/${id}/element-blocks`),
      api.get(`/admin/element-blocks/presets`).catch(() => ({ data: [] })),
    ]);
    const u = users.find((x) => x.id === id);
    setUser(u);
    setBlacklistText((u?.policy?.blacklist || []).join("\n"));
    setTimeForm({
      enabled: !!u?.policy?.time_restrictions_enabled,
      start: u?.policy?.work_hours_start || "09:00",
      end: u?.policy?.work_hours_end || "19:00",
      tz: u?.policy?.timezone || "Asia/Kolkata",
    });
    setGeoForm({
      enabled: !!u?.policy?.geo_enabled,
      lat: u?.policy?.geo_lat ?? "",
      lng: u?.policy?.geo_lng ?? "",
      radius: u?.policy?.geo_radius_m ?? 100,
    });
    setWmForm({
      opacity: u?.policy?.watermark_opacity ?? 0.15,
      size: u?.policy?.watermark_size ?? 14,
    });
    setVault(v);
    setHistory(h);
    setOverrides(ov);
    setBlocks(eb);
    setPresets(pp || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // v1.1.6: load activity data when user or date changes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/admin/users/${id}/activity`, { params: { date: activityDate } });
        if (!cancelled) setActivity(data);
      } catch { if (!cancelled) setActivity(null); }
    })();
    return () => { cancelled = true; };
  }, [id, activityDate]);

  if (!user) return <div className="p-10 text-zinc-500">Loading…</div>;

  const togglePolicy = async (key) => {
    const next = !user.policy[key];
    await api.put(`/users/${id}/policy`, { [key]: next });
    setUser({ ...user, policy: { ...user.policy, [key]: next } });
  };

  const saveBlacklist = async () => {
    const list = blacklistText.split("\n").map((s) => s.trim()).filter(Boolean);
    await api.put(`/users/${id}/policy`, { blacklist: list });
    setUser({ ...user, policy: { ...user.policy, blacklist: list } });
    flash();
  };

  const saveWatermark = async () => {
    const opacity = Math.min(0.5, Math.max(0.02, Number(wmForm.opacity) || 0.15));
    const size = Math.min(48, Math.max(8, parseInt(wmForm.size, 10) || 14));
    await api.put(`/users/${id}/policy`, { watermark_opacity: opacity, watermark_size: size });
    setUser({ ...user, policy: { ...user.policy, watermark_opacity: opacity, watermark_size: size } });
    flash();
  };

  const saveTime = async () => {
    await api.put(`/users/${id}/policy`, {
      time_restrictions_enabled: timeForm.enabled,
      work_hours_start: timeForm.start,
      work_hours_end: timeForm.end,
      timezone: timeForm.tz,
    });
    flash();
    load();
  };

  const saveGeo = async () => {
    const payload = {
      geo_enabled: geoForm.enabled,
      geo_radius_m: parseInt(geoForm.radius, 10) || 100,
    };
    if (geoForm.lat !== "") payload.geo_lat = parseFloat(geoForm.lat);
    if (geoForm.lng !== "") payload.geo_lng = parseFloat(geoForm.lng);
    await api.put(`/admin/users/${id}/geo`, payload);
    flash();
    load();
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) { alert("Browser geolocation unavailable"); return; }
    navigator.geolocation.getCurrentPosition((p) => {
      setGeoForm({ ...geoForm, lat: p.coords.latitude.toFixed(6), lng: p.coords.longitude.toFixed(6) });
    }, (err) => alert("Location error: " + err.message));
  };

  const revokeOverride = async (oid) => {
    if (!confirm("Revoke this geo approval?")) return;
    await api.delete(`/admin/geo-overrides/${oid}`);
    load();
  };

  // ---- Vault admin actions (v1.1.4) ----
  const addVaultEntry = async (e) => {
    e.preventDefault();
    if (!vaultForm.site.trim() || !vaultForm.username.trim() || !vaultForm.password) {
      alert("Site, username, and password are required.");
      return;
    }
    try {
      await api.post(`/admin/users/${id}/vault`, vaultForm);
      setVaultForm({ site: "", username: "", password: "", url: "", label: "" });
      flash();
      load();
    } catch (ex) { alert(formatErr(ex.response?.data?.detail)); }
  };

  const updateVaultEntry = async (vid, patch) => {
    try { await api.patch(`/admin/users/${id}/vault/${vid}`, patch); flash(); load(); }
    catch (ex) { alert(formatErr(ex.response?.data?.detail)); }
  };

  const deleteVaultEntry = async (vid) => {
    if (!confirm("Delete this credential? The employee will no longer get autofill on this site.")) return;
    try { await api.delete(`/admin/users/${id}/vault/${vid}`); flash(); load(); }
    catch (ex) { alert(formatErr(ex.response?.data?.detail)); }
  };

  const resetVaultPassword = async (vid) => {
    const np = prompt("New password for this credential (employee will autofill this; they cannot see it):");
    if (!np) return;
    await updateVaultEntry(vid, { password: np });
  };

  // ---- Element-block actions (v1.1.4) ----
  const addBlock = async (e) => {
    e.preventDefault();
    if (!blockForm.label.trim() || !blockForm.url_pattern.trim() || !blockForm.match_value.trim()) {
      alert("Label, URL pattern, and match value are required.");
      return;
    }
    try {
      await api.post(`/admin/users/${id}/element-blocks`, { ...blockForm, enabled: true });
      setBlockForm({ label: "", url_pattern: "", match_type: "label", match_value: "", action: "both" });
      flash();
      load();
    } catch (ex) { alert(formatErr(ex.response?.data?.detail)); }
  };

  const toggleBlock = async (rid, enabled) => {
    try { await api.patch(`/admin/users/${id}/element-blocks/${rid}`, { enabled }); load(); }
    catch (ex) { alert(formatErr(ex.response?.data?.detail)); }
  };

  const deleteBlock = async (rid) => {
    if (!confirm("Delete this element-block rule?")) return;
    try { await api.delete(`/admin/users/${id}/element-blocks/${rid}`); load(); }
    catch (ex) { alert(formatErr(ex.response?.data?.detail)); }
  };

  const applyPreset = async (key) => {
    try {
      const r = await api.post(`/admin/users/${id}/element-blocks/apply-preset?key=${encodeURIComponent(key)}`);
      if (r.data?.applied) {
        load();
      } else {
        alert("Preset is already applied to this user.");
      }
    } catch (ex) { alert(formatErr(ex.response?.data?.detail)); }
  };

  const flash = () => { setSavedMsg("Saved"); setTimeout(() => setSavedMsg(""), 1400); };

  const resetPassword = async () => {
    const pw = prompt("New password (min 6 chars):");
    if (!pw || pw.length < 6) return;
    try { await api.patch(`/admin/users/${id}`, { password: pw }); alert("Password updated"); }
    catch (ex) { alert(formatErr(ex.response?.data?.detail)); }
  };

  const forceLogout = async () => {
    if (!confirm(`Force-logout ${user.email}? Their browser will be kicked out within 60 seconds (instantly if chat is connected). All open tabs in their SND Secure Browser will return to the login screen.`)) return;
    try {
      await api.post(`/admin/users/${id}/force-logout`);
      alert("User has been force-logged-out. They will need to log in again.");
      load();
    } catch (ex) { alert(formatErr(ex.response?.data?.detail)); }
  };

  return (
    <div className="p-10 max-w-6xl">
      <Link to="/users" className="text-xs text-zinc-500 hover:text-zinc-300">← back to users</Link>
      <div className="flex items-end justify-between mt-2 mb-8">
        <div>
          <div className="text-xs tracking-widest text-emerald-400/80">USER</div>
          <h1 className="text-2xl font-semibold mt-1">{user.email}</h1>
          <div className="text-xs text-zinc-500">{user.name || "—"} · {user.role} · {user.active ? "active" : "disabled"}</div>
        </div>
        <div className="flex items-center gap-3">
          {savedMsg && <span className="text-xs text-emerald-400">{savedMsg}</span>}
          <button onClick={forceLogout} className="text-xs px-3 py-2 rounded border border-amber-700 text-amber-300 hover:bg-amber-900/20" data-testid="force-logout-btn" title="Invalidate this user's session immediately">Force logout</button>
          <button onClick={resetPassword} className="text-xs px-3 py-2 rounded border border-zinc-700 hover:border-zinc-500" data-testid="reset-password-btn">Reset password</button>
        </div>
      </div>

      {/* Activity timeline (v1.1.6) */}
      <div className="mb-6 rounded-lg border border-zinc-800 bg-[#0e0f14] p-5" data-testid="activity-chart-section">
        <div className="flex items-end justify-between mb-3">
          <div>
            <div className="text-xs tracking-widest text-zinc-500 uppercase">Activity timeline</div>
            <div className="text-[11px] text-zinc-500 mt-1">
              {activity ? (
                <>
                  Active <span className="text-emerald-300">{activity.totals.active_minutes_est} min</span> ·
                  {" "}{activity.totals.history} page visits ·
                  {" "}{activity.totals.events} endpoint events
                </>
              ) : "loading…"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input type="date" value={activityDate} max={todayISO()} onChange={(e) => setActivityDate(e.target.value)}
              data-testid="activity-date"
              className="text-xs px-2 py-1.5 rounded-md bg-zinc-900 border border-zinc-800" />
            <button onClick={() => setActivityDate(todayISO())} className="text-xs px-3 py-1.5 rounded-md border border-zinc-700 hover:border-zinc-500">Today</button>
          </div>
        </div>
        <div style={{ width: "100%", height: 180 }}>
          {activity && activity.buckets && activity.buckets.length > 0 ? (
            <ResponsiveContainer>
              <AreaChart data={activity.buckets} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="actGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.55} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#1f2937" strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: "#71717a", fontSize: 10 }} interval={11} axisLine={{ stroke: "#27272a" }} tickLine={false} />
                <YAxis tick={{ fill: "#71717a", fontSize: 10 }} allowDecimals={false} axisLine={{ stroke: "#27272a" }} tickLine={false} />
                <Tooltip
                  contentStyle={{ background: "#0a0a0a", border: "1px solid #27272a", borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: "#a1a1aa" }}
                  formatter={(v, n) => [v, n === "history" ? "page visits" : n === "events" ? "endpoint events" : "activity"]}
                />
                <Area type="monotone" dataKey="total" stroke="#34d399" strokeWidth={2} fill="url(#actGrad)" name="activity" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-sm text-zinc-500">
              No activity recorded on this day.
            </div>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Policy toggles */}
        <div className="rounded-lg border border-zinc-800 bg-[#0e0f14] p-5">
          <div className="text-xs tracking-widest text-zinc-500 uppercase mb-4">Restriction policy</div>
          <div className="space-y-1">
            {POLICY_FIELDS.map(([k, label]) => (
              <label key={k} className="flex items-center justify-between p-3 rounded-md hover:bg-zinc-900/40 cursor-pointer">
                <span className="text-sm text-zinc-200">{label}</span>
                <button type="button" data-testid={`policy-${k}`} onClick={() => togglePolicy(k)}
                  className={`w-10 h-6 rounded-full transition-colors relative ${user.policy[k] ? "bg-emerald-500" : "bg-zinc-700"}`}>
                  <span className={`absolute top-0.5 ${user.policy[k] ? "right-0.5" : "left-0.5"} w-5 h-5 rounded-full bg-white transition-all`} />
                </button>
              </label>
            ))}
          </div>
          {/* Watermark appearance — only relevant when watermark is on */}
          <div className={`mt-4 pt-4 border-t border-zinc-800 space-y-4 ${user.policy.show_watermark ? "" : "opacity-40 pointer-events-none"}`} data-testid="watermark-appearance">
            <div className="text-xs tracking-widest text-zinc-500 uppercase">Watermark appearance</div>
            <div>
              <div className="flex items-center justify-between text-sm text-zinc-300 mb-1">
                <span>Opacity</span>
                <span className="font-mono text-zinc-400">{Number(wmForm.opacity).toFixed(2)}</span>
              </div>
              <input type="range" min="0.02" max="0.5" step="0.01" value={wmForm.opacity}
                data-testid="watermark-opacity"
                onChange={(e) => setWmForm({ ...wmForm, opacity: parseFloat(e.target.value) })}
                className="w-full accent-emerald-500" />
            </div>
            <div>
              <div className="flex items-center justify-between text-sm text-zinc-300 mb-1">
                <span>Font size</span>
                <span className="font-mono text-zinc-400">{wmForm.size}px</span>
              </div>
              <input type="range" min="8" max="48" step="1" value={wmForm.size}
                data-testid="watermark-size"
                onChange={(e) => setWmForm({ ...wmForm, size: parseInt(e.target.value, 10) })}
                className="w-full accent-emerald-500" />
            </div>
            <button data-testid="save-watermark" onClick={saveWatermark}
              className="px-4 py-2 text-sm rounded-md bg-emerald-500 text-zinc-900 font-medium">Save watermark</button>
          </div>
        </div>

        {/* Blacklist */}
        <div className="rounded-lg border border-zinc-800 bg-[#0e0f14] p-5">
          <div className="text-xs tracking-widest text-zinc-500 uppercase mb-4">Site blacklist</div>
          <p className="text-xs text-zinc-500 mb-2">One domain per line (e.g. facebook.com). Matches include subdomains.</p>
          <textarea data-testid="blacklist-text" value={blacklistText} onChange={(e) => setBlacklistText(e.target.value)}
            className="w-full h-40 px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm font-mono"
            placeholder="facebook.com&#10;twitter.com&#10;youtube.com" />
          <button data-testid="save-blacklist" onClick={saveBlacklist} className="mt-3 px-4 py-2 text-sm rounded-md bg-emerald-500 text-zinc-900 font-medium">Save blacklist</button>
        </div>

        {/* Working hours */}
        <div className="rounded-lg border border-zinc-800 bg-[#0e0f14] p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs tracking-widest text-zinc-500 uppercase">Working hours</div>
            <label className="flex items-center gap-2 cursor-pointer">
              <span className="text-xs text-zinc-400">Enforced</span>
              <button type="button" data-testid="time-enabled" onClick={() => setTimeForm({ ...timeForm, enabled: !timeForm.enabled })}
                className={`w-10 h-6 rounded-full relative ${timeForm.enabled ? "bg-emerald-500" : "bg-zinc-700"}`}>
                <span className={`absolute top-0.5 ${timeForm.enabled ? "right-0.5" : "left-0.5"} w-5 h-5 rounded-full bg-white`} />
              </button>
            </label>
          </div>
          <p className="text-xs text-zinc-500 mb-3">Browser will lock outside these hours (user's timezone).</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-zinc-500">Start</label>
              <input data-testid="time-start" type="time" value={timeForm.start} onChange={(e) => setTimeForm({ ...timeForm, start: e.target.value })}
                className="w-full mt-1 px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm" />
            </div>
            <div>
              <label className="text-xs text-zinc-500">End</label>
              <input data-testid="time-end" type="time" value={timeForm.end} onChange={(e) => setTimeForm({ ...timeForm, end: e.target.value })}
                className="w-full mt-1 px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm" />
            </div>
            <div className="col-span-2">
              <label className="text-xs text-zinc-500">Timezone</label>
              <input value={timeForm.tz} onChange={(e) => setTimeForm({ ...timeForm, tz: e.target.value })}
                placeholder="Asia/Kolkata" className="w-full mt-1 px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm" />
            </div>
          </div>
          <button data-testid="save-time" onClick={saveTime} className="mt-3 px-4 py-2 text-sm rounded-md bg-emerald-500 text-zinc-900 font-medium">Save working hours</button>
        </div>

        {/* Geo restriction */}
        <div className="rounded-lg border border-zinc-800 bg-[#0e0f14] p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs tracking-widest text-zinc-500 uppercase">Allowed location</div>
            <label className="flex items-center gap-2 cursor-pointer">
              <span className="text-xs text-zinc-400">Enforced</span>
              <button type="button" data-testid="geo-enabled" onClick={() => setGeoForm({ ...geoForm, enabled: !geoForm.enabled })}
                className={`w-10 h-6 rounded-full relative ${geoForm.enabled ? "bg-emerald-500" : "bg-zinc-700"}`}>
                <span className={`absolute top-0.5 ${geoForm.enabled ? "right-0.5" : "left-0.5"} w-5 h-5 rounded-full bg-white`} />
              </button>
            </label>
          </div>
          <p className="text-xs text-zinc-500 mb-3">User must be within the radius. Out-of-zone users can request approval.</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-zinc-500">Latitude</label>
              <input data-testid="geo-lat" value={geoForm.lat} onChange={(e) => setGeoForm({ ...geoForm, lat: e.target.value })}
                placeholder="28.6139" className="w-full mt-1 px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm font-mono" />
            </div>
            <div>
              <label className="text-xs text-zinc-500">Longitude</label>
              <input data-testid="geo-lng" value={geoForm.lng} onChange={(e) => setGeoForm({ ...geoForm, lng: e.target.value })}
                placeholder="77.2090" className="w-full mt-1 px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm font-mono" />
            </div>
            <div>
              <label className="text-xs text-zinc-500">Radius (metres)</label>
              <input data-testid="geo-radius" type="number" min="10" value={geoForm.radius} onChange={(e) => setGeoForm({ ...geoForm, radius: e.target.value })}
                className="w-full mt-1 px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm" />
            </div>
            <div className="flex items-end">
              <button onClick={useCurrentLocation} className="w-full px-3 py-2 text-xs rounded-md border border-zinc-700 hover:border-zinc-500">Use my current location</button>
            </div>
          </div>
          <button data-testid="save-geo" onClick={saveGeo} className="mt-3 px-4 py-2 text-sm rounded-md bg-emerald-500 text-zinc-900 font-medium">Save location</button>

          {overrides.length > 0 && (
            <div className="mt-5">
              <div className="text-xs text-zinc-500 mb-2">Active approvals</div>
              <div className="space-y-1">
                {overrides.map((o) => (
                  <div key={o.id} className="flex justify-between items-center p-2 text-xs bg-zinc-900/50 border border-zinc-800 rounded">
                    <div>
                      <div className="text-zinc-300">{o.expires_at ? `until ${new Date(o.expires_at).toLocaleString()}` : "Forever"}</div>
                      <div className="text-zinc-500">granted by {o.granted_by}</div>
                    </div>
                    <button onClick={() => revokeOverride(o.id)} className="text-red-400 px-2 py-1 border border-red-900/60 rounded">Revoke</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Element-block rules (v1.1.4) */}
      <div className="mt-6 rounded-lg border border-zinc-800 bg-[#0e0f14] p-5" data-testid="element-blocks-section">
        <div className="flex items-center justify-between mb-1">
          <div className="text-xs tracking-widest text-zinc-500 uppercase">Element click blocks <span className="text-emerald-400/70 normal-case">· hide confidential UI on third-party sites</span></div>
        </div>
        <p className="text-xs text-zinc-500 mb-4">Each rule hides or disables a specific element on pages matching the URL pattern. Useful for blocking fee calculators, export buttons, etc. Reaches into open shadow roots.</p>

        {presets && presets.length > 0 && (
          <div className="mb-5 p-3 rounded-md bg-emerald-500/5 border border-emerald-500/20" data-testid="preset-strip">
            <div className="text-[11px] text-emerald-300 uppercase tracking-widest mb-2">Quick presets · one-click</div>
            <div className="flex flex-wrap gap-2">
              {presets.map((p) => (
                <button
                  key={p.key}
                  data-testid={`preset-apply-${p.key}`}
                  onClick={() => applyPreset(p.key)}
                  className="text-xs px-3 py-1.5 rounded-full border border-emerald-700/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20"
                  title={`${p.url_pattern} · ${p.action}`}
                >
                  + {p.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {blocks.length === 0 && <div className="text-sm text-zinc-500 mb-3">No rules yet.</div>}
        <div className="space-y-2 mb-5">
          {blocks.map((r) => (
            <div key={r.id} className="p-3 rounded-md bg-zinc-900/40 border border-zinc-800 grid grid-cols-12 items-center gap-3 text-sm" data-testid={`block-row-${r.id}`}>
              <div className="col-span-4 min-w-0">
                <div className="text-zinc-200 truncate">{r.label}</div>
                <div className="text-[11px] text-zinc-500 truncate">{r.url_pattern}</div>
              </div>
              <div className="col-span-3 text-xs text-zinc-400 truncate">
                <span className="px-1.5 py-0.5 rounded bg-black/40 border border-zinc-800 mr-1">{r.match_type}</span>
                <code className="text-[11px]">{r.match_value}</code>
              </div>
              <div className="col-span-2 text-xs text-zinc-400">
                <span className="px-1.5 py-0.5 rounded bg-amber-500/10 border border-amber-700/40 text-amber-300">{r.action}</span>
              </div>
              <div className="col-span-3 flex items-center justify-end gap-2">
                <button onClick={() => toggleBlock(r.id, !r.enabled)} data-testid={`block-toggle-${r.id}`}
                  className={`w-10 h-5 rounded-full relative ${r.enabled ? "bg-emerald-500" : "bg-zinc-700"}`}>
                  <span className={`absolute top-0.5 ${r.enabled ? "right-0.5" : "left-0.5"} w-4 h-4 rounded-full bg-white`} />
                </button>
                <button onClick={() => deleteBlock(r.id)} data-testid={`block-delete-${r.id}`}
                  className="text-xs text-red-400 px-2 py-1 border border-red-900/60 rounded hover:bg-red-900/20">Delete</button>
              </div>
            </div>
          ))}
        </div>

        <form onSubmit={addBlock} className="grid grid-cols-12 gap-3 items-end pt-4 border-t border-zinc-800" data-testid="block-add-form">
          <div className="col-span-3">
            <label className="text-xs text-zinc-500">Label</label>
            <input data-testid="block-label" value={blockForm.label} onChange={(e) => setBlockForm({ ...blockForm, label: e.target.value })}
              placeholder='e.g. "Amazon — Fees"' className="w-full mt-1 px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm" />
          </div>
          <div className="col-span-3">
            <label className="text-xs text-zinc-500">URL pattern (glob)</label>
            <input data-testid="block-url" value={blockForm.url_pattern} onChange={(e) => setBlockForm({ ...blockForm, url_pattern: e.target.value })}
              placeholder="sellercentral.amazon.in/*" className="w-full mt-1 px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm font-mono" />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-zinc-500">Match by</label>
            <select data-testid="block-match-type" value={blockForm.match_type} onChange={(e) => setBlockForm({ ...blockForm, match_type: e.target.value })}
              className="w-full mt-1 px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm">
              <option value="label">label / aria-label</option>
              <option value="text">visible text</option>
              <option value="css">CSS selector</option>
              <option value="attr">attribute (name=value)</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className="text-xs text-zinc-500">Value</label>
            <input data-testid="block-value" value={blockForm.match_value} onChange={(e) => setBlockForm({ ...blockForm, match_value: e.target.value })}
              placeholder="Calculate revenue" className="w-full mt-1 px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm" />
          </div>
          <div className="col-span-1">
            <label className="text-xs text-zinc-500">Action</label>
            <select data-testid="block-action" value={blockForm.action} onChange={(e) => setBlockForm({ ...blockForm, action: e.target.value })}
              className="w-full mt-1 px-2 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm">
              <option value="hide">hide</option>
              <option value="disable_click">disable</option>
              <option value="both">both</option>
            </select>
          </div>
          <div className="col-span-1">
            <button type="submit" data-testid="block-add-submit"
              className="w-full px-3 py-2 text-sm rounded-md bg-emerald-500 text-zinc-900 font-medium">Add</button>
          </div>
        </form>
      </div>

      {/* Vault — admin-managed (v1.1.4) */}
      <div className="mt-6 rounded-lg border border-zinc-800 bg-[#0e0f14] p-5" data-testid="vault-section">
        <div className="text-xs tracking-widest text-zinc-500 uppercase mb-1">Password vault <span className="text-emerald-400/70 normal-case">· admin manages, employee can only autofill</span></div>
        <p className="text-xs text-zinc-500 mb-4">Add credentials here and the employee will get one-click autofill on the matching site. They never see the plaintext password.</p>

        {vault.length === 0 && <div className="text-sm text-zinc-500 mb-3">No saved credentials.</div>}
        <div className="space-y-2 mb-5">
          {vault.map((v) => (
            <div key={v.id} className="p-3 rounded-md bg-zinc-900/40 border border-zinc-800 flex items-center justify-between text-sm" data-testid={`vault-row-${v.id}`}>
              <div className="min-w-0">
                <div className="text-zinc-200 truncate">{v.site}</div>
                <div className="text-xs text-zinc-500 truncate">{v.username} {v.label && `· ${v.label}`}</div>
              </div>
              <div className="flex items-center gap-2">
                <code className="text-xs font-mono px-2 py-1 rounded bg-black/40 border border-zinc-800">{reveal[v.id] ? v.password : "••••••••"}</code>
                <button data-testid={`reveal-${v.id}`} onClick={() => setReveal({ ...reveal, [v.id]: !reveal[v.id] })}
                  className="text-xs px-2 py-1 rounded border border-zinc-700 hover:border-zinc-500">{reveal[v.id] ? "Hide" : "Reveal"}</button>
                <button data-testid={`vault-reset-${v.id}`} onClick={() => resetVaultPassword(v.id)}
                  className="text-xs px-2 py-1 rounded border border-zinc-700 hover:border-zinc-500">Reset pwd</button>
                <button data-testid={`vault-delete-${v.id}`} onClick={() => deleteVaultEntry(v.id)}
                  className="text-xs text-red-400 px-2 py-1 border border-red-900/60 rounded hover:bg-red-900/20">Delete</button>
              </div>
            </div>
          ))}
        </div>

        <form onSubmit={addVaultEntry} className="grid grid-cols-12 gap-3 items-end pt-4 border-t border-zinc-800" data-testid="vault-add-form">
          <div className="col-span-3">
            <label className="text-xs text-zinc-500">Site (host)</label>
            <input data-testid="vault-site" value={vaultForm.site} onChange={(e) => setVaultForm({ ...vaultForm, site: e.target.value })}
              placeholder="sellercentral.amazon.in" className="w-full mt-1 px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm font-mono" />
          </div>
          <div className="col-span-3">
            <label className="text-xs text-zinc-500">Username / email</label>
            <input data-testid="vault-username" value={vaultForm.username} onChange={(e) => setVaultForm({ ...vaultForm, username: e.target.value })}
              placeholder="user@example.com" className="w-full mt-1 px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm" />
          </div>
          <div className="col-span-3">
            <label className="text-xs text-zinc-500">Password</label>
            <input data-testid="vault-password" type="password" value={vaultForm.password} onChange={(e) => setVaultForm({ ...vaultForm, password: e.target.value })}
              placeholder="••••••••" className="w-full mt-1 px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm" />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-zinc-500">Label (optional)</label>
            <input data-testid="vault-label" value={vaultForm.label} onChange={(e) => setVaultForm({ ...vaultForm, label: e.target.value })}
              placeholder="Personal / Team" className="w-full mt-1 px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm" />
          </div>
          <div className="col-span-1">
            <button type="submit" data-testid="vault-add-submit"
              className="w-full px-3 py-2 text-sm rounded-md bg-emerald-500 text-zinc-900 font-medium">Add</button>
          </div>
        </form>
      </div>

      {/* History */}
      <div className="mt-6 rounded-lg border border-zinc-800 bg-[#0e0f14] p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="text-xs tracking-widest text-zinc-500 uppercase">URL history ({history.length})</div>
          <div className="flex gap-2">
            <button
              data-testid="purge-user-history-older"
              onClick={async () => {
                const daysStr = prompt(`Delete history entries older than how many days?\n(Type a number — e.g. 7 = wipe everything older than a week)`, "30");
                if (!daysStr) return;
                const days = parseInt(daysStr, 10);
                if (!Number.isInteger(days) || days < 1) return alert("Please enter a positive whole number.");
                const r = await api.delete(`/admin/users/${id}/history?older_than_days=${days}`);
                alert(`Deleted ${r.data.deleted} rows older than ${days} days for this user.`);
                load();
              }}
              className="text-xs px-3 py-1.5 rounded border border-zinc-700 hover:bg-zinc-800 text-zinc-300"
            >Purge older than…</button>
            <button
              data-testid="purge-user-history-all"
              onClick={async () => {
                if (!confirm(`Delete ALL browsing history for ${user.email}?\n\nThis cannot be undone.`)) return;
                const r = await api.delete(`/admin/users/${id}/history`);
                alert(`Deleted ${r.data.deleted} history rows for ${user.email}.`);
                load();
              }}
              className="text-xs px-3 py-1.5 rounded border border-red-900/60 text-red-300 hover:bg-red-900/20"
            >Delete all history</button>
          </div>
        </div>
        {history.length === 0 && <div className="text-sm text-zinc-500">No browsing activity recorded.</div>}
        <div className="divide-y divide-zinc-900 max-h-96 overflow-auto">
          {history.map((h) => (
            <div key={h.id} className="py-2 flex justify-between text-sm">
              <div className="min-w-0">
                <div className="text-zinc-200 truncate">{h.title || h.url}</div>
                <div className="text-xs text-zinc-500 truncate">{h.url}</div>
              </div>
              <div className="text-[11px] text-zinc-500 shrink-0 ml-4">{new Date(h.ts).toLocaleString()}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
